import { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { useFeedback } from '../../hooks/useFeedback';
import { useUserStore, scopedStorageKey } from '../../store/userStore';
import type { ContentType } from '../../lib/feedback';

const MAX_COMMENT = 280;

// Scoped to entityId+userId (see userStore.scopedStorageKey) — contentId
// alone let every user/tenant in a shared browser profile see and vote
// through one shared `__octopm_thumbs_{contentId}` key, which both
// pre-voted the widget for users who never voted and (via the `submitted`
// guard below) blocked them from voting at all. Returns null when the
// entity/user scope isn't known yet, in which case the vote simply isn't
// persisted for this render.
function storageKey(
  contentId: string,
  entityId: string | undefined,
  userId: string | undefined,
): string | null {
  const scoped = scopedStorageKey('__octopm_thumbs', entityId, userId);
  return scoped ? `${scoped}:${contentId}` : null;
}

interface ThumbsWidgetProps {
  contentType: ContentType;
  contentId: string;
  compact?: boolean;
}

export function ThumbsWidget({ contentType, contentId, compact = false }: ThumbsWidgetProps) {
  const [vote, setVote] = useState<'up' | 'down' | null>(null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { submit, isSubmitting } = useFeedback();
  const entityId = useUserStore((s) => s.sessionConfig?.entityId);
  const userId = useUserStore((s) => s.sessionConfig?.userId);
  const key = storageKey(contentId, entityId, userId);

  // Restore persisted vote on mount, and re-check whenever the identity or
  // content scope changes (e.g. switching entity/persona in the same tab).
  useEffect(() => {
    if (!key) {
      setVote(null);
      setSubmitted(false);
      return;
    }
    try {
      const stored = localStorage.getItem(key);
      if (stored === 'up' || stored === 'down') {
        setVote(stored);
        setSubmitted(true);
      } else {
        setVote(null);
        setSubmitted(false);
      }
    } catch {
      // ignore
    }
  }, [key]);

  const persist = (v: 'up' | 'down') => {
    if (!key) return;
    try {
      localStorage.setItem(key, v);
    } catch {
      // ignore
    }
  };

  const handleUp = async () => {
    if (submitted) return;
    setVote('up');
    setShowComment(false);
    setSubmitError(null);
    try {
      await submit({
        feedbackType: 'usefulness',
        usefulness: { contentType, contentId, useful: true },
      });
      persist('up');
      setSubmitted(true);
    } catch {
      setSubmitError("Couldn't save");
      setVote(null);
    }
  };

  const handleDown = () => {
    if (submitted) return;
    setVote('down');
    setShowComment(true);
    setSubmitError(null);
  };

  const handleDownSubmit = async () => {
    if (submitted) return;
    setSubmitError(null);
    try {
      await submit({
        feedbackType: 'usefulness',
        usefulness: {
          contentType,
          contentId,
          useful: false,
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        },
      });
      persist('down');
      setSubmitted(true);
      setShowComment(false);
    } catch {
      setSubmitError("Couldn't save");
    }
  };

  return (
    <div className="flex flex-col gap-1.5 mt-1.5">
      <div className="flex items-center gap-1.5">
        {!compact && (
          <span className="text-micro text-gray-500 dark:text-gray-600">Helpful?</span>
        )}
        <button
          type="button"
          onClick={() => void handleUp()}
          disabled={isSubmitting || submitted}
          aria-label="Thumbs up"
          className={`p-0.5 rounded transition-colors disabled:opacity-50 ${
            vote === 'up'
              ? 'text-emerald-500'
              : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
          }`}
        >
          <ThumbsUp className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={handleDown}
          disabled={isSubmitting || submitted}
          aria-label="Thumbs down"
          className={`p-0.5 rounded transition-colors disabled:opacity-50 ${
            vote === 'down'
              ? 'text-red-400'
              : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
          }`}
        >
          <ThumbsDown className="h-4 w-4" />
        </button>
        {submitted && (
          <span className="text-micro text-gray-400 dark:text-gray-500">Thanks</span>
        )}
        {submitError && (
          <span className="text-micro text-red-400 dark:text-red-500">{submitError}</span>
        )}
      </div>

      {showComment && !submitted && (
        <div className="flex flex-col gap-1.5">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
            placeholder="What could be better? (optional)"
            rows={2}
            maxLength={MAX_COMMENT}
            disabled={isSubmitting}
            className="w-full px-2.5 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-micro text-gray-700 dark:text-gray-300 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 resize-none disabled:opacity-60"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-micro text-gray-400 dark:text-gray-600">
              {comment.length}/{MAX_COMMENT}
            </span>
            <button
              type="button"
              onClick={() => void handleDownSubmit()}
              disabled={isSubmitting}
              className="flex items-center gap-1 px-3 py-1 bg-primary text-white rounded-lg text-micro font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
